import { useEffect, useMemo, useState } from 'react';
import { useAuthStore } from '@/lib/auth-store';
import { useThemeStore } from '@/lib/theme-store';
import { cn } from '@/lib/cn';
import type { EPCR, Code99EventType, Code99Rhythm } from '@/types';
import {
  Activity, Heart, Zap, Wind, Pill, Syringe, Clipboard, CheckCircle2,
  Skull, FileText, HeartPulse, X, AlertTriangle,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useCode99Store } from './code99-store';
import { ACLSAlgorithmPanel } from './ACLSAlgorithmPanel';
import { ClosureModal } from './ClosureModal';
import { computeCompliance } from './compliance';

/**
 * Code99Mode — cardiac-arrest resuscitation workspace.
 *
 * Three-zone layout per the addendum:
 *
 *   Zone 1 (top):    Running clocks — Time Since Arrest, Time Since CPR,
 *                    Time to Next Rhythm Check, Time to Next Epi, CPR Quality.
 *                    Always visible while the session is active.
 *
 *   Zone 2 (center): Event log — reverse-chronological list of every
 *                    contemporaneous entry. Large-tap event buttons across
 *                    the bottom open quick-entry overlays.
 *
 *   Zone 3 (right):  ACLS algorithm guide — branch (shockable /
 *                    non-shockable / ROSC / termination) auto-updates
 *                    based on the most recently logged rhythm. H's & T's
 *                    always available.
 *
 * Session A (this commit) lands the scaffold + clocks + event log + core
 * event buttons wired to the zustand store. Session B will add the
 * Zone-3 algorithm guide, Session C the compliance locks + narrative.
 */

interface Props {
  epcr: EPCR;
  agencyId: string;
  incidentId: string;
  onExit: () => void;
}

export function Code99Mode({ epcr, agencyId, incidentId, onExit }: Props) {
  const { user } = useAuthStore();
  const { session, events, loading, loadByEpcr, activate, logEvent, updateSession } = useCode99Store();
  const [quickEntry, setQuickEntry] = useState<Code99EventType | null>(null);
  const [showClosure, setShowClosure] = useState(false);

  const compliance = useMemo(() => computeCompliance(session, events), [session, events]);

  useEffect(() => {
    void loadByEpcr(epcr.id);
  }, [epcr.id, loadByEpcr]);

  async function ensureActive() {
    if (session) return session;
    if (!user) return null;
    return activate({
      agencyId, incidentId, epcrId: epcr.id, userId: user.id,
      reason: 'manual',
    });
  }

  async function openEntry(type: Code99EventType) {
    await ensureActive();
    setQuickEntry(type);
  }

  async function simpleLog(type: Code99EventType, details: Record<string, unknown> = {}) {
    await ensureActive();
    await logEvent({
      eventType: type,
      details,
      performedBy: user?.id,
      enteredBy: user?.id,
    });
  }

  if (loading) {
    return <div className="p-6 text-center text-sm text-slate-500">Loading Code 99 session…</div>;
  }

  return (
    <div className="fixed inset-0 z-40 flex flex-col bg-slate-950">
      {/* Top bar */}
      <div className="flex items-center justify-between border-b border-red-700 bg-red-950/40 px-3 py-1.5">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center bg-red-600 text-white">
            <HeartPulse size={16} />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="text-sm font-black uppercase tracking-wider text-red-200">Code 99 · Cardiac Arrest</span>
            <span className="text-[10px] font-mono text-red-300/80">
              ePCR {epcr.id.slice(0, 8)} · {session ? `activated ${new Date(session.activated_at).toLocaleTimeString()}` : 'not activated'}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {/* Close session button — only meaningful once there's a session */}
          {session && !session.closed_at && (
            <button
              onClick={() => setShowClosure(true)}
              className={cn(
                'px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider border',
                compliance.canClose
                  ? 'border-emerald-500 bg-emerald-700 text-white hover:bg-emerald-600'
                  : 'border-amber-600 bg-amber-700/60 text-amber-100 hover:bg-amber-700',
              )}
              title={compliance.canClose
                ? 'All compliance items met — close Code 99'
                : `${compliance.blockersFailing} required item(s) pending · click to see checklist`}
            >
              Close Code 99
            </button>
          )}
          {session?.closed_at && (
            <span className="px-2 py-1 text-[10px] font-mono uppercase tracking-widest bg-slate-800 text-slate-400 border border-slate-700">
              closed {new Date(session.closed_at).toLocaleTimeString([], { hour12: false })}
            </span>
          )}
          <button onClick={onExit} className="h-9 w-9 flex items-center justify-center text-red-200 hover:bg-red-900/50">
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Zone 1 — Running Clocks */}
      <RunningClocks />

      {/* Zones 2 + 3 */}
      <div className="flex-1 grid grid-cols-1 ipadpro:grid-cols-[1fr_320px] overflow-hidden">
        {/* Zone 2 — Event log + buttons */}
        <div className="flex flex-col overflow-hidden border-r border-slate-800">
          <div className="flex-1 overflow-y-auto">
            <EventLog />
          </div>
          <EventButtonBar
            onRhythm={()     => openEntry('rhythm')}
            onShock={()      => openEntry('shock')}
            onCprStart={()   => simpleLog('cpr_start')}
            onCprStop={()    => simpleLog('cpr_stop',  { reason: 'rhythm_check' })}
            onAirway={()     => openEntry('airway')}
            onMedication={() => openEntry('medication')}
            onIVIO={()       => openEntry('iv_io')}
            onTwelveLead={() => openEntry('twelve_lead')}
            onROSC={async () => {
              await ensureActive();
              await logEvent({ eventType: 'rosc', performedBy: user?.id });
              await updateSession({ disposition: 'rosc' });
            }}
            onTermination={() => openEntry('termination')}
            onNote={()       => openEntry('note')}
          />
        </div>

        {/* Zone 3 — live ACLS algorithm guide + H's & T's */}
        <aside className="hidden ipadpro:block overflow-y-auto bg-slate-900/50 p-2 space-y-2">
          <SessionDetails />
          <ACLSAlgorithmPanel />
        </aside>
      </div>

      {/* Quick-entry modals */}
      {quickEntry && (
        <QuickEntryModal
          type={quickEntry}
          onClose={() => setQuickEntry(null)}
          onSubmit={async (details) => {
            await ensureActive();
            await logEvent({
              eventType: quickEntry,
              details,
              performedBy: user?.id,
              enteredBy: user?.id,
            });
            setQuickEntry(null);
          }}
        />
      )}

      {/* Closure modal — compliance checklist + narrative review */}
      {showClosure && (
        <ClosureModal
          onDismiss={() => setShowClosure(false)}
          onClosed={() => {
            setShowClosure(false);
            // Keep the workspace open on-screen with a "closed" badge
            // so the user can still review. `onExit` returns to the ePCR.
          }}
        />
      )}
    </div>
  );
}

// ============================================================
// Zone 1 — Running Clocks
// ============================================================
function RunningClocks() {
  const { session, events } = useCode99Store();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);

  const cprStart = session?.cpr_started_at ? new Date(session.cpr_started_at).getTime() : null;
  const arrestAt = session?.arrest_time ? new Date(session.arrest_time).getTime() : null;

  // Last rhythm check = most recent 'rhythm' event; next check target is +2:00.
  const lastRhythm = events.find((e) => e.event_type === 'rhythm');
  const nextRhythmAt = lastRhythm
    ? new Date(lastRhythm.event_time).getTime() + 2 * 60_000
    : cprStart ? cprStart + 2 * 60_000 : null;

  // Last epi = most recent medication event with name starting with 'Epi'.
  const lastEpi = events.find(
    (e) => e.event_type === 'medication' &&
    typeof e.details.name === 'string' &&
    (e.details.name as string).toLowerCase().startsWith('epi'),
  );
  const nextEpiAt = lastEpi
    ? new Date(lastEpi.event_time).getTime() + 3 * 60_000
    : null;

  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-slate-800 border-b border-slate-800">
      <Clock title="Time Since Arrest"  remaining={arrestAt ? now - arrestAt : null} tone="neutral" countUp />
      <Clock title="Time Since CPR"     remaining={cprStart ? now - cprStart : null} tone={cprStart ? 'blue' : 'muted'} countUp />
      <Clock
        title="Next Rhythm Check"
        remaining={nextRhythmAt ? nextRhythmAt - now : null}
        tone={nextRhythmAt && nextRhythmAt - now < 15_000 ? 'red' : nextRhythmAt && nextRhythmAt - now < 30_000 ? 'amber' : 'emerald'}
      />
      <Clock
        title="Next Epi (q 3-5 min)"
        remaining={nextEpiAt ? nextEpiAt - now : null}
        tone={nextEpiAt && nextEpiAt - now < 0 ? 'red' : nextEpiAt && nextEpiAt - now < 30_000 ? 'amber' : 'emerald'}
      />
      <CPRQualityClock cprStart={cprStart} />
    </div>
  );
}

/**
 * CPRQualityClock — shows the most recent device-reported CPR quality
 * stream (rate / depth / fraction) when a LifePak or ZOLL is
 * connected and streaming via the device-ingest edge function.
 *
 * Falls back to the manual "adequate / inadequate" tap-select when
 * no device data has arrived yet. Green when all three metrics are
 * in target, amber when one is marginal, red when any is out of
 * range (AHA 2020 targets: rate 100–120, depth 2.0–2.4 in / 5–6 cm,
 * fraction ≥ 60%).
 */
function CPRQualityClock({ cprStart }: { cprStart: number | null }) {
  const { events } = useCode99Store();
  // Device CPR-quality notes carry details.cpr_quality (see
  // device-ingest edge function). Pick the most recent.
  const latest = events.find(
    (e) => e.event_type === 'note'
      && (e.details?.source === 'device')
      && !!e.details?.cpr_quality,
  );
  const q = latest?.details?.cpr_quality as {
    mean_rate_per_min?: number;
    mean_depth_mm?: number;
    compression_fraction_pct?: number;
  } | undefined;

  if (!cprStart) {
    return (
      <div className="px-2 py-1.5 bg-slate-950">
        <div className="text-[9px] font-bold uppercase tracking-widest text-slate-500">CPR Quality</div>
        <div className="font-mono tabular-nums text-xl font-black leading-none mt-0.5 text-slate-600">—</div>
        <div className="text-[9px] font-mono text-slate-500 mt-0.5">not started</div>
      </div>
    );
  }

  if (!q) {
    return (
      <div className="px-2 py-1.5 bg-slate-950">
        <div className="text-[9px] font-bold uppercase tracking-widest text-slate-500">CPR Quality</div>
        <div className="font-mono tabular-nums text-xl font-black leading-none mt-0.5 text-slate-300">manual</div>
        <div className="text-[9px] font-mono text-slate-500 mt-0.5">no device feed</div>
      </div>
    );
  }

  // Target ranges — AHA 2020
  const rate = q.mean_rate_per_min ?? 0;
  const depth = q.mean_depth_mm ?? 0;
  const frac = q.compression_fraction_pct ?? 0;
  const rateOk  = rate >= 100 && rate <= 120;
  const depthOk = depth >= 50 && depth <= 60;  // 5–6 cm in mm
  const fracOk  = frac >= 60;
  const tone: 'emerald' | 'amber' | 'red' =
    (rateOk && depthOk && fracOk) ? 'emerald'
    : (rateOk || depthOk || fracOk) ? 'amber'
    : 'red';
  const toneBg = tone === 'emerald' ? 'bg-emerald-950/30'
    : tone === 'amber' ? 'bg-amber-950/30'
    : 'bg-red-950/40';
  const toneText = tone === 'emerald' ? 'text-emerald-300'
    : tone === 'amber' ? 'text-amber-300'
    : 'text-red-300';

  return (
    <div className={cn('px-2 py-1.5 bg-slate-950', toneBg)}>
      <div className={cn('text-[9px] font-bold uppercase tracking-widest', toneText)}>CPR Quality</div>
      <div className="flex items-baseline gap-1.5 mt-0.5">
        <span className={cn('font-mono tabular-nums text-sm font-bold', rateOk ? 'text-slate-100' : 'text-amber-300')}>
          {Math.round(rate)}/min
        </span>
        <span className={cn('font-mono tabular-nums text-sm font-bold', depthOk ? 'text-slate-100' : 'text-amber-300')}>
          {(depth / 10).toFixed(1)}cm
        </span>
        <span className={cn('font-mono tabular-nums text-sm font-bold', fracOk ? 'text-slate-100' : 'text-amber-300')}>
          {Math.round(frac)}%
        </span>
      </div>
      <div className="text-[9px] font-mono text-slate-500 mt-0.5">rate · depth · fraction</div>
    </div>
  );
}

function Clock({
  title, remaining, tone, footer, countUp = false,
}: {
  title: string;
  remaining: number | null;
  tone: 'neutral' | 'blue' | 'emerald' | 'amber' | 'red' | 'muted';
  footer?: string;
  countUp?: boolean;
}) {
  const cockpitV2 = useThemeStore((s) => s.cockpitV2);
  const display = remaining === null
    ? '—'
    : formatMMSS(Math.max(0, countUp ? remaining : remaining));
  const overdue = !countUp && remaining !== null && remaining < 0;
  return (
    <div className={cn(
      'px-2 py-1.5 bg-slate-950',
      tone === 'red' && 'bg-red-950/60',
      tone === 'amber' && 'bg-amber-950/40',
      tone === 'emerald' && 'bg-emerald-950/30',
      tone === 'blue' && 'bg-blue-950/30',
    )}>
      <div className={cn(
        'text-[9px] font-bold uppercase tracking-widest',
        tone === 'red' ? 'text-red-300' :
        tone === 'amber' ? 'text-amber-300' :
        tone === 'emerald' ? 'text-emerald-300' :
        tone === 'blue' ? 'text-blue-300' : 'text-slate-500',
      )}>{title}</div>
      <div className={cn(
        'tabular-nums leading-none mt-0.5',
        // cockpit-v2: heavier display font + larger size on the timer
        // so it reads as the primary attention object on the screen
        // (per spec §3.6 — Zone 1 timer in display font, 80px).
        // Falls back to mono + 2xl when v2 is off (existing behavior).
        cockpitV2
          ? '[font-family:var(--font-display)] text-[40px] font-bold tracking-tight'
          : 'font-mono text-2xl font-black tracking-tight',
        overdue ? 'text-red-400' : 'text-slate-100',
      )}>{display}</div>
      {footer && <div className="text-[9px] font-mono text-slate-500 mt-0.5 truncate">{footer}</div>}
    </div>
  );
}

function formatMMSS(ms: number): string {
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

// ============================================================
// Zone 2 — Event log
// ============================================================
function EventLog() {
  const { events } = useCode99Store();
  if (events.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-center text-slate-500 text-sm p-6">
        No events yet. Tap an action below to start the contemporaneous log.
      </div>
    );
  }
  // Foundation §8.9 — event log rows alternate between #1E2530 / #1A2028
  // with a 2-px colored left border per event type. The index-based
  // alternation runs in display order; events are already sorted
  // newest-first by the store.
  return (
    <div className="bg-[#1a2028]">
      {events.map((e, i) => (
        <EventRow key={e.id} event={e} alt={i % 2 === 1} />
      ))}
    </div>
  );
}

// §8.9 signature-screen DNA: every row carries a 2-px left border
// keyed to event type. Rhythm = amber, Shock = red, CPR = green,
// Med = blue, Airway = violet, ROSC = bright green, Term = red.
const EVENT_ROW_ACCENT: Record<Code99EventType, string> = {
  rhythm:           'border-l-[#e8a020]',
  shock:            'border-l-[#e03e3e]',
  cpr_start:        'border-l-[#2d9d5f]',
  cpr_resume:       'border-l-[#2d9d5f]',
  cpr_stop:         'border-l-[#8b5f0e]',
  airway:           'border-l-[#8b5cf6]',
  medication:       'border-l-[#2d7ab5]',
  iv_io:            'border-l-[#2d7ab5]',
  twelve_lead:      'border-l-[#e8a020]',
  rosc:             'border-l-[#2dd66b]',
  termination:      'border-l-[#e03e3e]',
  note:             'border-l-[#3d4a5c]',
  reversible_cause: 'border-l-[#8b5cf6]',
};

function EventRow({ event, alt }: { event: import('@/types').Code99Event; alt: boolean }) {
  const time = new Date(event.event_time).toLocaleTimeString([], { hour12: false });
  const summary = summarizeEvent(event);
  return (
    <div
      className={cn(
        // §8.9 row structure: mono timestamp · uppercase tracking-widest
        // event label · plain-text summary · optional "retro" pill.
        'flex items-start gap-2 px-3 py-2 border-l-2 border-b border-b-slate-800/60',
        EVENT_ROW_ACCENT[event.event_type] ?? 'border-l-slate-700',
        alt ? 'bg-[#1e2530]' : 'bg-[#1a2028]',
      )}
    >
      <span className="font-mono text-[12px] tabular-nums text-[#e8a020] w-20 shrink-0 leading-snug">
        {time}
      </span>
      <span className={cn(
        'text-[10px] font-bold uppercase tracking-widest w-16 shrink-0 pt-0.5',
        eventTone(event.event_type),
      )}>
        {prettyType(event.event_type)}
      </span>
      <span className="flex-1 text-sm text-slate-100 leading-snug">{summary}</span>
      {event.retroactive && (
        <span className="status-pill bg-amber-950 text-amber-300 border border-amber-800 shrink-0">retro</span>
      )}
    </div>
  );
}

function prettyType(t: Code99EventType): string {
  return {
    rhythm: 'Rhythm', shock: 'Shock',
    cpr_start: 'CPR ▶', cpr_stop: 'CPR ⏸', cpr_resume: 'CPR ▶',
    airway: 'Airway', medication: 'Med', iv_io: 'Access',
    twelve_lead: '12-Ld', rosc: 'ROSC', termination: 'Term',
    note: 'Note', reversible_cause: "H's & T's",
  }[t];
}

function eventTone(t: Code99EventType): string {
  switch (t) {
    case 'rosc': return 'text-emerald-300';
    case 'shock': return 'text-amber-300';
    case 'termination': return 'text-red-300';
    case 'medication': return 'text-blue-300';
    case 'cpr_start': case 'cpr_resume': return 'text-emerald-300';
    case 'cpr_stop': return 'text-amber-300';
    default: return 'text-slate-400';
  }
}

function summarizeEvent(e: import('@/types').Code99Event): string {
  const d = e.details ?? {};
  switch (e.event_type) {
    case 'rhythm':   return `Rhythm: ${d.rhythm ?? '—'}`;
    case 'shock':    return `Defibrillation at ${d.joules ?? '?'} J${d.device_confirmed ? ' (device)' : ''}`;
    case 'cpr_start':return 'CPR initiated';
    case 'cpr_stop': return `CPR paused${d.reason ? ` — ${d.reason}` : ''}`;
    case 'cpr_resume':return 'CPR resumed';
    case 'airway':   return `${d.type ?? 'Airway'}${d.size ? ` (${d.size})` : ''}${d.etco2_reading ? ` · EtCO2 ${d.etco2_reading}` : ''}`;
    case 'medication': return `${d.name ?? 'Medication'} ${d.dose ?? ''}${d.unit ?? ''} ${d.route ?? ''}`.trim();
    case 'iv_io':    return `${d.type ?? 'Access'} at ${d.site ?? '?'}, ${d.gauge ?? '?'}g — ${d.success ? 'successful' : 'attempt'}`;
    case 'twelve_lead':return `12-lead acquired${d.stemi ? ' · STEMI pattern' : ''}`;
    case 'rosc':     return 'Return of spontaneous circulation';
    case 'termination':return 'Resuscitation terminated';
    case 'note':     return String(d.text ?? '');
    case 'reversible_cause':return `H/T ${d.cause ?? ''} ${d.status ?? ''}`.trim();
  }
}

// ============================================================
// Zone 2 — Event Button Bar
// ============================================================
interface ButtonBarProps {
  onRhythm: () => void; onShock: () => void;
  onCprStart: () => void; onCprStop: () => void;
  onAirway: () => void; onMedication: () => void;
  onIVIO: () => void; onTwelveLead: () => void;
  onROSC: () => void; onTermination: () => void;
  onNote: () => void;
}
function EventButtonBar(p: ButtonBarProps) {
  return (
    <div className="border-t border-slate-700 bg-slate-950 p-1.5 grid grid-cols-3 md:grid-cols-6 xl:grid-cols-11 gap-1">
      <ActionButton label="Rhythm"    icon={Heart}       onClick={p.onRhythm}        tone="slate" />
      <ActionButton label="Shock"     icon={Zap}         onClick={p.onShock}         tone="amber" />
      <ActionButton label="CPR ▶"     icon={Activity}    onClick={p.onCprStart}      tone="emerald" />
      <ActionButton label="CPR ⏸"     icon={Activity}    onClick={p.onCprStop}       tone="slate" />
      <ActionButton label="Airway"    icon={Wind}        onClick={p.onAirway}        tone="slate" />
      <ActionButton label="Med"       icon={Pill}        onClick={p.onMedication}    tone="blue" />
      <ActionButton label="IV / IO"   icon={Syringe}     onClick={p.onIVIO}          tone="slate" />
      <ActionButton label="12-Lead"   icon={Clipboard}   onClick={p.onTwelveLead}    tone="slate" />
      <ActionButton label="ROSC"      icon={CheckCircle2}onClick={p.onROSC}          tone="emerald" big />
      <ActionButton label="Terminate" icon={Skull}       onClick={p.onTermination}   tone="red" />
      <ActionButton label="Note"      icon={FileText}    onClick={p.onNote}          tone="slate" />
    </div>
  );
}

function ActionButton({
  label, icon: Icon, onClick, tone, big,
}: {
  label: string;
  icon: LucideIcon;
  onClick: () => void;
  tone: 'slate' | 'amber' | 'emerald' | 'red' | 'blue';
  big?: boolean;
}) {
  const toneCls = {
    slate:   'bg-slate-800 hover:bg-slate-700 text-slate-100 border-slate-600',
    amber:   'bg-amber-600 hover:bg-amber-500 text-white border-amber-400',
    emerald: 'bg-emerald-600 hover:bg-emerald-500 text-white border-emerald-400',
    red:     'bg-red-700 hover:bg-red-600 text-white border-red-500',
    blue:    'bg-blue-600 hover:bg-blue-500 text-white border-blue-400',
  }[tone];
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex flex-col items-center justify-center gap-0.5 border py-2 px-1 text-[11px] font-bold uppercase tracking-wider transition-colors min-h-[60px]',
        toneCls,
        big && 'col-span-2 md:col-span-1',
      )}
    >
      <Icon size={18} />
      {label}
    </button>
  );
}

// ============================================================
// Zone 3 — Session Details (arrest time capture)
// ============================================================
function SessionDetails() {
  const { session, updateSession } = useCode99Store();
  if (!session) return null;

  const arrestSet = !!session.arrest_time;

  return (
    <div className="border border-slate-700 bg-slate-900">
      <div className="border-b border-slate-800 px-2 py-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-300">
        Session Context
      </div>
      <div className="p-2 space-y-1.5">
        {arrestSet ? (
          <div className="text-[11px] text-slate-300">
            <div className="flex items-center justify-between">
              <span className="text-slate-500 font-mono uppercase tracking-widest text-[9px]">Arrest Time</span>
              <button
                onClick={() => updateSession({ arrest_time: null, arrest_time_source: null })}
                className="text-[10px] text-slate-500 hover:text-amber-300"
              >
                reset
              </button>
            </div>
            <div className="font-mono tabular-nums text-slate-200">
              {new Date(session.arrest_time!).toLocaleTimeString([], { hour12: false })}
              <span className="ml-1 text-[10px] text-slate-500">· {session.arrest_time_source}</span>
            </div>
          </div>
        ) : (
          <ArrestTimePrompt />
        )}
      </div>
    </div>
  );
}

function ArrestTimePrompt() {
  const { updateSession } = useCode99Store();
  const setNow = (source: 'witnessed' | 'bystander' | 'estimated') =>
    updateSession({ arrest_time: new Date().toISOString(), arrest_time_source: source });
  return (
    <div className="space-y-1.5">
      <div className="text-[11px] text-slate-300 leading-snug">
        When did the arrest occur? (Needed for Time-Since-Arrest clock.)
      </div>
      <div className="grid grid-cols-3 gap-1">
        <button
          onClick={() => setNow('witnessed')}
          className="border border-slate-600 bg-slate-800 text-slate-200 hover:bg-slate-700 py-1.5 text-[10px] font-bold uppercase tracking-wider"
          title="Witnessed by crew on arrival (now)"
        >
          Witnessed
        </button>
        <button
          onClick={() => setNow('bystander')}
          className="border border-slate-600 bg-slate-800 text-slate-200 hover:bg-slate-700 py-1.5 text-[10px] font-bold uppercase tracking-wider"
          title="Bystander reports arrest occurred now"
        >
          Bystander
        </button>
        <button
          onClick={() => updateSession({ arrest_time_source: 'unknown' })}
          className="border border-slate-600 bg-slate-800 text-slate-400 hover:bg-slate-700 py-1.5 text-[10px] font-bold uppercase tracking-wider"
          title="Arrest time unknown"
        >
          Unknown
        </button>
      </div>
      <div className="text-[10px] text-slate-500">
        Or enter a specific time:
      </div>
      <input
        type="datetime-local"
        onChange={(e) => {
          if (!e.target.value) return;
          const iso = new Date(e.target.value).toISOString();
          void updateSession({ arrest_time: iso, arrest_time_source: 'estimated' });
        }}
        className="input-field text-sm py-1 w-full"
      />
    </div>
  );
}

// ============================================================
// Quick-entry modal
// ============================================================
function QuickEntryModal({
  type, onClose, onSubmit,
}: {
  type: Code99EventType;
  onClose: () => void;
  onSubmit: (details: Record<string, unknown>) => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-3">
      <div className="w-full max-w-md border border-slate-700 bg-slate-900 shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-700 bg-slate-950 px-3 py-1.5">
          <span className="text-[11px] font-bold uppercase tracking-widest text-slate-200">{prettyType(type)} · Quick Entry</span>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-200"><X size={14} /></button>
        </div>
        <div className="p-3">
          {type === 'rhythm' && <RhythmEntry onSubmit={onSubmit} />}
          {type === 'shock' && <ShockEntry onSubmit={onSubmit} />}
          {type === 'airway' && <AirwayEntry onSubmit={onSubmit} />}
          {type === 'medication' && <MedicationEntry onSubmit={onSubmit} />}
          {type === 'iv_io' && <IVIOEntry onSubmit={onSubmit} />}
          {type === 'twelve_lead' && <TwelveLeadEntry onSubmit={onSubmit} />}
          {type === 'termination' && <TerminationEntry onSubmit={onSubmit} />}
          {type === 'note' && <NoteEntry onSubmit={onSubmit} />}
        </div>
      </div>
    </div>
  );
}

function RhythmEntry({ onSubmit }: { onSubmit: (d: Record<string, unknown>) => void }) {
  const rhythms: Code99Rhythm[] = ['VF', 'PVT', 'PEA', 'Asystole', 'Organized', 'ROSC'];
  return (
    <div className="grid grid-cols-2 gap-1.5">
      {rhythms.map((r) => (
        <button key={r} onClick={() => onSubmit({ rhythm: r })}
          className="border border-slate-600 bg-slate-800 text-slate-100 py-3 text-sm font-bold hover:bg-slate-700">
          {r}
        </button>
      ))}
    </div>
  );
}

function ShockEntry({ onSubmit }: { onSubmit: (d: Record<string, unknown>) => void }) {
  const joules = [120, 150, 200, 300, 360];
  return (
    <div className="space-y-1.5">
      <p className="text-[11px] text-slate-400">Select energy delivered:</p>
      <div className="grid grid-cols-5 gap-1.5">
        {joules.map((j) => (
          <button key={j} onClick={() => onSubmit({ joules: j, energy_source: 'manual' })}
            className="border border-amber-500 bg-amber-600 text-white py-3 text-sm font-bold hover:bg-amber-500">
            {j}J
          </button>
        ))}
      </div>
    </div>
  );
}

function AirwayEntry({ onSubmit }: { onSubmit: (d: Record<string, unknown>) => void }) {
  const types = ['BVM', 'OPA', 'NPA', 'Supraglottic (i-gel)', 'Supraglottic (King LT)', 'Supraglottic (LMA)', 'Intubation'];
  const [detail, setDetail] = useState<{ type?: string; size?: string; depth?: string; etco2?: string; attempts?: string }>({});
  const needsSub = detail.type?.startsWith('Intub') || detail.type?.startsWith('Supra');
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-1.5">
        {types.map((t) => (
          <button key={t}
            onClick={() => setDetail({ ...detail, type: t })}
            className={cn(
              'border py-2 px-1 text-xs font-bold',
              detail.type === t ? 'border-blue-400 bg-blue-900/40 text-blue-100' : 'border-slate-600 bg-slate-800 text-slate-200',
            )}>
            {t}
          </button>
        ))}
      </div>
      {needsSub && (
        <div className="grid grid-cols-2 gap-1.5">
          <input className="input-field" placeholder="Size (e.g. 7.5)" value={detail.size ?? ''}
            onChange={(e) => setDetail({ ...detail, size: e.target.value })} />
          <input className="input-field" placeholder="Depth at teeth (cm)" value={detail.depth ?? ''}
            onChange={(e) => setDetail({ ...detail, depth: e.target.value })} />
          <input className="input-field" placeholder="EtCO2 reading" value={detail.etco2 ?? ''}
            onChange={(e) => setDetail({ ...detail, etco2: e.target.value })} />
          <input className="input-field" placeholder="# attempts" inputMode="numeric" value={detail.attempts ?? ''}
            onChange={(e) => setDetail({ ...detail, attempts: e.target.value })} />
        </div>
      )}
      <button disabled={!detail.type} onClick={() => onSubmit(detail)} className="btn-primary w-full">
        Log Airway
      </button>
    </div>
  );
}

function MedicationEntry({ onSubmit }: { onSubmit: (d: Record<string, unknown>) => void }) {
  const acls = [
    { name: 'Epinephrine', dose: '1', unit: 'mg',  route: 'IV/IO' },
    { name: 'Epinephrine (Pediatric)', dose: '0.01', unit: 'mg/kg', route: 'IV/IO' },
    { name: 'Amiodarone (1st)', dose: '300', unit: 'mg', route: 'IV/IO' },
    { name: 'Amiodarone (2nd)', dose: '150', unit: 'mg', route: 'IV/IO' },
    { name: 'Lidocaine', dose: '1–1.5', unit: 'mg/kg', route: 'IV/IO' },
    { name: 'Magnesium Sulfate', dose: '1–2', unit: 'g', route: 'IV/IO' },
    { name: 'Sodium Bicarbonate', dose: '1', unit: 'mEq/kg', route: 'IV/IO' },
    { name: 'Calcium Chloride', dose: '1', unit: 'g', route: 'IV/IO' },
    { name: 'Dextrose 50%', dose: '25', unit: 'g', route: 'IV/IO' },
    { name: 'Naloxone', dose: '0.4–2', unit: 'mg', route: 'IV/IO/IN' },
  ];
  return (
    <div className="grid grid-cols-1 gap-1.5">
      {acls.map((m) => (
        <button key={m.name} onClick={() => onSubmit(m)}
          className="flex items-center justify-between border border-slate-600 bg-slate-800 px-2.5 py-1.5 text-left hover:bg-slate-700">
          <span className="text-sm font-semibold text-slate-100">{m.name}</span>
          <span className="font-mono text-xs text-blue-300">{m.dose} {m.unit} {m.route}</span>
        </button>
      ))}
    </div>
  );
}

function IVIOEntry({ onSubmit }: { onSubmit: (d: Record<string, unknown>) => void }) {
  const [d, setD] = useState<{ type?: string; site?: string; gauge?: string; attempts?: string; success?: boolean }>({});
  const sites = ['AC', 'Forearm', 'Hand', 'EJ', 'Tibial (IO)', 'Humeral (IO)'];
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-1.5">
        {['IV', 'IO'].map((t) => (
          <button key={t} onClick={() => setD({ ...d, type: t })}
            className={cn('border py-2 font-bold', d.type === t ? 'border-blue-400 bg-blue-900/40 text-blue-100' : 'border-slate-600 bg-slate-800 text-slate-200')}>
            {t}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        {sites.map((s) => (
          <button key={s} onClick={() => setD({ ...d, site: s })}
            className={cn('border py-2 text-xs', d.site === s ? 'border-blue-400 bg-blue-900/40 text-blue-100' : 'border-slate-600 bg-slate-800 text-slate-200')}>
            {s}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        <input className="input-field" placeholder="Gauge (e.g. 18)" value={d.gauge ?? ''} onChange={(e) => setD({ ...d, gauge: e.target.value })} />
        <input className="input-field" placeholder="# attempts" value={d.attempts ?? ''} onChange={(e) => setD({ ...d, attempts: e.target.value })} />
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        <button onClick={() => onSubmit({ ...d, success: true })}  className="btn-primary">Successful</button>
        <button onClick={() => onSubmit({ ...d, success: false })} className="btn-secondary">Unsuccessful</button>
      </div>
    </div>
  );
}

function TwelveLeadEntry({ onSubmit }: { onSubmit: (d: Record<string, unknown>) => void }) {
  const [stemi, setStemi] = useState(false);
  const [interp, setInterp] = useState('');
  return (
    <div className="space-y-2">
      <label className="flex items-center gap-2 text-sm text-slate-200">
        <input type="checkbox" checked={stemi} onChange={(e) => setStemi(e.target.checked)} />
        STEMI pattern detected
      </label>
      <textarea className="input-field" rows={3} placeholder="Brief interpretation (optional)"
        value={interp} onChange={(e) => setInterp(e.target.value)} />
      <button onClick={() => onSubmit({ stemi, interpretation: interp || null, device_imported: false })}
        className="btn-primary w-full">
        Log 12-Lead
      </button>
    </div>
  );
}

function TerminationEntry({ onSubmit }: { onSubmit: (d: Record<string, unknown>) => void }) {
  const { updateSession } = useCode99Store();
  const [d, setD] = useState({
    med_cmd_physician_name: '',
    med_cmd_physician_badge: '',
    time_of_death: new Date().toISOString().slice(0, 16),
    family_notified: 'deferred_to_receiving_facility' as 'yes' | 'no' | 'unknown' | 'deferred_to_receiving_facility',
    law_enforcement_on_scene: false,
  });
  return (
    <div className="space-y-2">
      <div className="flex items-start gap-2 border border-red-700/60 bg-red-950/40 px-2 py-1.5 text-xs text-red-200">
        <AlertTriangle size={13} className="mt-0.5 text-red-400 shrink-0" />
        Terminating resuscitation. All fields below are required medico-legally.
      </div>
      <input className="input-field" placeholder="Medical command physician name"
        value={d.med_cmd_physician_name} onChange={(e) => setD({ ...d, med_cmd_physician_name: e.target.value })} />
      <input className="input-field" placeholder="Physician badge / ID"
        value={d.med_cmd_physician_badge} onChange={(e) => setD({ ...d, med_cmd_physician_badge: e.target.value })} />
      <label className="block text-[10px] uppercase tracking-wider text-slate-500">Time of Death</label>
      <input className="input-field" type="datetime-local"
        value={d.time_of_death} onChange={(e) => setD({ ...d, time_of_death: e.target.value })} />
      <label className="block text-[10px] uppercase tracking-wider text-slate-500">Family Notified</label>
      <select className="input-field" value={d.family_notified}
        onChange={(e) => setD({ ...d, family_notified: e.target.value as typeof d.family_notified })}>
        <option value="yes">Yes — on scene</option>
        <option value="no">No</option>
        <option value="unknown">Unknown</option>
        <option value="deferred_to_receiving_facility">Deferred to receiving facility</option>
      </select>
      <label className="flex items-center gap-2 text-sm text-slate-200">
        <input type="checkbox" checked={d.law_enforcement_on_scene}
          onChange={(e) => setD({ ...d, law_enforcement_on_scene: e.target.checked })} />
        Law enforcement on scene
      </label>
      <button
        disabled={!d.med_cmd_physician_name || !d.med_cmd_physician_badge}
        onClick={async () => {
          const tod = new Date(d.time_of_death).toISOString();
          await updateSession({
            disposition: 'termination',
            med_cmd_contacted: true,
            med_cmd_physician_name: d.med_cmd_physician_name,
            med_cmd_physician_badge: d.med_cmd_physician_badge,
            med_cmd_contact_time: new Date().toISOString(),
            time_of_death: tod,
            family_notified: d.family_notified,
            law_enforcement_on_scene: d.law_enforcement_on_scene,
          });
          onSubmit({ time_of_death: tod, med_cmd: d.med_cmd_physician_name, badge: d.med_cmd_physician_badge });
        }}
        className="btn-danger w-full">
        Log Termination
      </button>
    </div>
  );
}

function NoteEntry({ onSubmit }: { onSubmit: (d: Record<string, unknown>) => void }) {
  const [text, setText] = useState('');
  return (
    <div className="space-y-2">
      <textarea className="input-field font-mono text-sm" rows={4} autoFocus
        placeholder="Free-text note (bystander CPR duration, scene findings, etc.)"
        value={text} onChange={(e) => setText(e.target.value)} />
      <button disabled={!text.trim()} onClick={() => onSubmit({ text: text.trim() })} className="btn-primary w-full">
        Log Note
      </button>
    </div>
  );
}

